import React from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
import _, { isNull } from 'underscore';
import { makeStyles } from '@mui/styles';
import { DataTable } from 'components';
import EditIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import {
	FormGroup,
	FormLabel,
	Typography,
	IconButton,
	Tooltip
} from '@mui/material';
import DatePicker from 'components/DatePicker/DatePicker';
import { ToggleIncidentTrackerDialog, ToggleDeleteIncidentDialog } from '../../actions/PagerDutyIncidentsActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	empty: {
		color: 'grey',
		fontStyle: 'italic'
	},
	cellWidth: {
		width: 100
	},
	customHeader: {
		color: '#263238',
		fontSize: '14px',
		fontFamily: 'Roboto, Helvetica, Arial sans-serif',
		fontWeight: 500,
		lineHeight: 1.75,
		letterSpacing: '0.02857em'
	},
	customFilter: {
		marginTop: 10,
	},
}));

const PagerDutyIncidentsTable = (props) => {
	const { data, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();

	const _onEditIncidentClick = (incidentId, incidentClosed) => {
		dispatch(ToggleIncidentTrackerDialog(true, incidentId, incidentClosed));
	};

	const _onDeleteIncidentClick = (incidentId) => {
		dispatch(ToggleDeleteIncidentDialog(true, incidentId));
	};

	const _displayTableMeta = (value) => console.log(value);

	const _getTableColumns = () => (
		[
			{
				name: 'internalId',
				label: 'Internal Id',
				options: {
					display: false,
					filterType: 'textField',
					searchable: false,
					filter: false,
					sortThirdClickReset: true,
					viewColumns: false
				},
			},
			{
				name: 'id',
				label: 'ID',
				options: {
					display: true,
					filterType: 'textField',
					sortThirdClickReset: true,
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
				},
			},
			{
				name: 'incident_number',
				label: 'Incident Number',
				options: {
					display: true,
					filterType: 'textField',
					sortThirdClickReset: true,
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Incident Number
						</Typography>
					),
				},
			},
			{
				name: 'description',
				label: 'Description',
				options: {
					display: true,
					filterType: 'textField',
					sortThirdClickReset: true,
				},
			},
			{
				name: 'service_id',
				label: 'Service Id',
				options: {
					display: false,
					filterType: 'textField',
					sortThirdClickReset: true,
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Service Id
						</Typography>
					),
					searchable: false
				},
			},
			{
				name: 'service_name',
				label: 'Service Name',
				options: {
					display: true,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Service Name
						</Typography>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'escalation_policy_id',
				label: 'Escalation Policy Id',
				options: {
					display: false,
					filterType: 'textField',
					sortThirdClickReset: true,
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Escalation Policy Id
						</Typography>
					),
					searchable: false
				},
			},
			{
				name: 'escalation_policy_name',
				label: 'Escalation Policy Name',
				options: {
					display: false,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					sortThirdClickReset: true,
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Escalation Policy Name
						</Typography>
					),
					searchable: false
				},
			},
			{
				name: 'created_on',
				label: 'Created On',
				options: {
					display: true,
					filterType: 'custom',
					searchable: false,
					customBodyRender: (value, _tableMeta, _updateValue) => (
						isNull(value)
							? <div className={clsx(classes.empty)}>(empty)</div>
							: <>{moment(value).format('YYYY-MM-DD HH:mm:ss').toString()}</>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Created On
						</Typography>
					),
					filterOptions: {
						logic: (date, filters) => {
							if (filters[0] && filters[1]) {
								return date < filters[0] || date > filters[1];
							}
							if (filters[0]) {
								return date < filters[0];
							}
							if (filters[1]) {
								return date > filters[1];
							}
							return false;
						},
						display: (filterList, onChange, index, column) => (
							<div>
								<FormLabel>Created On</FormLabel>
								<FormGroup row>
									<DatePicker
										className={clsx(classes.customFilter)}
										label="From"
										size="small"
										value={filterList[index][0] || ''}
										onChange={(date, keyboardInput) => {
											// eslint-disable-next-line no-param-reassign
											filterList[index][0] = date?.format('YYYY-MM-DD') ?? keyboardInput;
											onChange(filterList[index], index, column);
										}}
									/>
									<DatePicker
										className={clsx(classes.customFilter)}
										label="To"
										size="small"
										value={filterList[index][1] || ''}
										onChange={(date, keyboardInput) => {
											// eslint-disable-next-line no-param-reassign
											filterList[index][1] = date?.format('YYYY-MM-DD') ?? keyboardInput;
											onChange(filterList[index], index, column);
										}}
									/>
								</FormGroup>
							</div>
						)
					},
					sortThirdClickReset: true,
				}
			},
			{
				name: 'resolved_on',
				label: 'Resolved On',
				options: {
					display: true,
					filterType: 'custom',
					filterOptions: {
						logic: (date, filters) => {
							if (filters[0] && filters[1]) {
								return date < filters[0] || date > filters[1];
							}
							if (filters[0]) {
								return date < filters[0];
							}
							if (filters[1]) {
								return date > filters[1];
							}
							return false;
						},
						display: (filterList, onChange, index, column) => (
							<div>
								<FormLabel>Resolved On</FormLabel>
								<FormGroup row>
									<DatePicker
										className={clsx(classes.customFilter)}
										label="From"
										size="small"
										value={filterList[index][0] || ''}
										onChange={(date, keyboardInput) => {
											// eslint-disable-next-line no-param-reassign
											filterList[index][0] = date?.format('YYYY-MM-DD') ?? keyboardInput;
											onChange(filterList[index], index, column);
										}}
									/>
									<DatePicker
										className={clsx(classes.customFilter)}
										label="To"
										size="small"
										value={filterList[index][1] || ''}
										onChange={(date, keyboardInput) => {
											// eslint-disable-next-line no-param-reassign
											filterList[index][1] = date?.format('YYYY-MM-DD') ?? keyboardInput;
											onChange(filterList[index], index, column);
										}}
									/>
								</FormGroup>
							</div>
						)
					},
					searchable: false,
					customBodyRender: (value, _tableMeta, _updateValue) => (
						isNull(value)
							? <div className={clsx(classes.empty)}>(empty)</div>
							: <>{moment(value).format('YYYY-MM-DD HH:mm:ss').toString()}</>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Resolved On
						</Typography>
					),
					sortThirdClickReset: true,
				}
			},
			{
				name: 'seconds_to_first_ack',
				label: 'Seconds to First Acknowledgement',
				options: {
					display: true,
					filterType: 'textField',
					sortThirdClickReset: true,
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Seconds to First Acknowledgement
						</Typography>
					),
					searchable: false
				},
			},
			{
				name: 'seconds_to_resolve',
				label: 'Seconds to Resolve',
				options: {
					display: true,
					filterType: 'textField',
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Seconds to Resolve
						</Typography>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'urgency',
				label: 'Urgency',
				options: {
					display: false,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'priority',
				label: 'Priority',
				options: {
					display: true,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'status',
				label: 'Status',
				options: {
					display: false,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'initial_response_sla',
				label: 'Initial Response SLA',
				options: {
					display: true,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Initial Response SLA
						</Typography>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'resolution_sla',
				label: 'Resolution SLA',
				options: {
					display: true,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Resolution SLA
						</Typography>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'end_to_end_sla',
				label: 'End to End SLA',
				options: {
					display: true,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							End to End SLA
						</Typography>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'stakeholder',
				label: 'Stakeholder',
				options: {
					display: true,
					filterType: 'multiselect',
					filterOptions: {
						renderValue: v => v ?? '(empty)'
					},
					customBodyRender: (value, _tableMeta, _updateValue) => (
						value ?? <div className={clsx(classes.empty)}>(empty)</div>
					),
					sortThirdClickReset: true,
					searchable: false
				},
			},
			{
				name: 'lastUpdate',
				label: 'Last Update',
				options: {
					display: false,
					filterType: 'textField',
					searchable: false,
					filter: false,
					viewColumns: false,
					sortThirdClickReset: true,
					customBodyRender: (value, _tableMeta, _updateValue) => (
						isNull(value)
							? <div className={clsx(classes.empty)}>(empty)</div>
							: <>{moment(value).format('YYYY-MM-DD HH:mm:ss').toString()}</>
					)
				}
			},
			{
				name: 'alarm_Ref',
				label: 'Alarm Ref',
				options: {
					display: true,
					filterType: 'textField',
					sortThirdClickReset: true,
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Alarm Ref
						</Typography>
					),
				},
			},
			{
				name: 'severity',
				label: 'Severity',
				options: {
					display: true,
					filterType: 'textField',
					sortThirdClickReset: true,
					customHeadLabelRender: (_columnMeta) => (
						<Typography className={clsx(classes.customHeader)} component="span" noWrap>
							Severity
						</Typography>
					),
				},
			},
		]
	);

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		const statusIndex = _getTableColumns().findIndex(column => column.name === 'status');
		const isClosed = rowData[statusIndex] === 'Closed';
		return (
			<>
				<Tooltip title="Edit">
					<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _onEditIncidentClick(rowData[0], isClosed)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Remove">
					<IconButton edge="end" size="small" className={classes.errorButton} onClick={() => _onDeleteIncidentClick(rowData[0]) /* _onDeleteIncidentClick(rowData[0]) */}>
						<CancelIcon />
					</IconButton>
				</Tooltip>
			</>
		);
	};

	const _getTableColumnsWithActions = () => {
		const columns = _getTableColumns();
		return [
			...columns,
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			}
		];
	};

	return (
		<div {...rest} className={clsx(classes.root)}>
			<DataTable
				id="pagerDutyIncidentsTable"
				title=""
				data={data}
				columns={_getTableColumnsWithActions()}
				rowsPerPage={10}
				tableBodyMaxHeight="21em"
				tableBodyCellMaxWidth="250"
				options={{
					searchPlaceholder: 'Search ID, Incident Number or Description...',
				}}
			/>
		</div>
	);
};

PagerDutyIncidentsTable.propTypes = {
	data: PropTypes.array.isRequired
};

export default PagerDutyIncidentsTable;
